'use client'

/**
 * Documentation section for /products/avery-sw900: the two official Avery
 * Dennison PDFs Peter supplied (scripts/supplier-assets/intake-avery-docs/),
 * converted to HTML and images rather than embedded as PDFs or offered as
 * download-only links.
 *
 * - Colour swatch poster: rendered to two webp sizes by
 *   scripts/supplier-assets/wire-product-docs.ts and uploaded to S3. Shown
 *   here as a click-to-expand image (URLs below are constants -- no DB
 *   Media row backs them).
 * - Product Data Sheet (Revision 20, dated 03/10/26): transcribed BY HAND
 *   into the DocSection blocks below, verbatim -- every number, unit, and
 *   claim copied exactly from `pdftotext -layout
 *   scripts/supplier-assets/intake-avery-docs/pds-sw-900-wrapping-film.pdf`,
 *   in the PDF's own section order. The only edits made to the extracted
 *   text are: (a) inserting a space where the PDF's text layer runs two
 *   words together with no space (e.g. "119 µm)Reference" ->
 *   "119 µm). Reference"), and (b) per house style, replacing the two
 *   en-dashes the PDF prints inside a numeric range with a hyphen
 *   ("0.7–1.5 kg/cm" -> "0.7-1.5 kg/cm", "Satin – 20-30 GU" ->
 *   "Satin - 20-30 GU"). No figure, unit, or word was added, removed, or
 *   rounded.
 *
 * See scripts/supplier-assets/intake-avery-docs/sources.json for
 * provenance and authorization.
 */
import { Disclosure, DisclosureGroup } from '@heroui/react'
import type { ReactNode } from 'react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Modal } from '@/components/ui/modal'

const POSTER_DISPLAY_URL = 'https://media.choice.graphics/product-docs/avery-sw900/color-swatch-poster-display.webp'
const POSTER_FULL_URL = 'https://media.choice.graphics/product-docs/avery-sw900/color-swatch-poster-full.webp'
const POSTER_ALT = 'Avery Dennison SW900 colour swatch poster'

// ---------------------------------------------------------------------
// Presentational helpers
// ---------------------------------------------------------------------

function KeyValueTable({ rows }: { rows: Array<{ label: string; value: ReactNode }> }) {
  return (
    <div className='rounded-lg border border-divider overflow-hidden'>
      {rows.map((row, i) => (
        <div
          key={row.label}
          className={`flex flex-col gap-1 px-4 py-2.5 text-sm sm:flex-row sm:gap-6 ${i % 2 === 0 ? 'bg-overlay' : ''}`}
        >
          <span className='text-muted font-medium shrink-0 sm:w-2/5'>{row.label}</span>
          <span className='text-foreground'>{row.value}</span>
        </div>
      ))}
    </div>
  )
}

function BulletList({ items }: { items: ReactNode[] }) {
  return (
    <ul className='list-disc space-y-1.5 pl-5 text-foreground/80'>
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  )
}

interface ColorRow {
  code: string
  color: string
  extra: string
}

function ColorTable({ headers, rows }: { headers: [string, string, string]; rows: ColorRow[] }) {
  return (
    <div className='overflow-x-auto rounded-lg border border-divider'>
      <table className='w-full min-w-[520px] border-collapse text-sm'>
        <thead>
          <tr className='border-b border-divider text-left'>
            {headers.map(h => (
              <th key={h} className='px-3 py-2 font-medium text-muted'>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.code} className={i % 2 === 0 ? 'bg-overlay' : ''}>
              <td className='px-3 py-2 whitespace-nowrap text-foreground'>{row.code}</td>
              <td className='px-3 py-2 text-foreground'>{row.color}</td>
              <td className='px-3 py-2 text-foreground'>{row.extra}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

interface DocSectionProps {
  id: string
  title: string
  children: ReactNode
}

function DocSection({ id, title, children }: DocSectionProps) {
  return (
    <Disclosure id={id} aria-label={title} className='overflow-hidden rounded-lg border border-divider bg-surface'>
      <Disclosure.Heading>
        {/* transform-none: HeroUI scales a pressed button to 0.97, which on a
            full-width trigger inside an overflow-hidden card leaves a sliver of
            card showing around the pressed fill. cw-ui-heading: the trigger
            sits in the Disclosure's h3, which the theme sets in Anton; the
            title stays in the body face (spec 3.2 component-heading opt-out). */}
        <Button
          slot='trigger'
          variant='ghost'
          className='h-auto w-full justify-between rounded-none px-4 py-3.5 text-left transform-none'
        >
          <span className='cw-ui-heading text-base font-semibold text-foreground'>{title}</span>
          <Disclosure.Indicator className='text-muted' />
        </Button>
      </Disclosure.Heading>
      <Disclosure.Content>
        <Disclosure.Body className='space-y-3 border-t border-divider px-4 py-4 text-sm text-foreground/80'>
          {children}
        </Disclosure.Body>
      </Disclosure.Content>
    </Disclosure>
  )
}

// ---------------------------------------------------------------------
// Durability table data (page 6 of the PDF)
// ---------------------------------------------------------------------

const DURABILITY_ROWS: Array<{
  category: string
  zone1Vertical: string
  zone1Horizontal: string
  zone2Vertical: string
  zone2Horizontal: string
  zone3Vertical: string
  zone3Horizontal: string
}> = [
  { category: 'SW900 Black & White', zone1Vertical: '12 years', zone1Horizontal: '3 years', zone2Vertical: '9.6 years', zone2Horizontal: '6 months', zone3Vertical: '5.8 years', zone3Horizontal: '4 months' },
  { category: 'SW900 Color', zone1Vertical: '10 years', zone1Horizontal: '2 years', zone2Vertical: '8 years', zone2Horizontal: '6 months', zone3Vertical: '4.8 years', zone3Horizontal: '4 months' },
  { category: 'SW900 Metallic & Pearlescent -including Rugged', zone1Vertical: '5 years', zone1Horizontal: '2 years', zone2Vertical: '4 years', zone2Horizontal: '6 months', zone3Vertical: '4.8 years', zone3Horizontal: '4 months' },
  { category: 'SW900 Metallic (with gold flake)*', zone1Vertical: '3 years', zone1Horizontal: '1 year', zone2Vertical: '2.4 years', zone2Horizontal: '6 months', zone3Vertical: '1.5 years', zone3Horizontal: '4 months' },
  { category: 'SW900X Textured Films', zone1Vertical: '4 years', zone1Horizontal: '1 year', zone2Vertical: '3 years', zone2Horizontal: '6 months', zone3Vertical: '2 years', zone3Horizontal: '4 months' },
  { category: 'SW900 Diamond Line', zone1Vertical: '3 years', zone1Horizontal: '1 year', zone2Vertical: '2.4 years', zone2Horizontal: '6 months', zone3Vertical: '1.5 years', zone3Horizontal: '4 months' },
  { category: 'SW900 Color Flow Series', zone1Vertical: '4 years', zone1Horizontal: '1 year', zone2Vertical: '3 years', zone2Horizontal: '6 months', zone3Vertical: '2 years', zone3Horizontal: '4 months' }
]

const GOLD_FLAKE_COLORS = [
  'SW900-199 Gloss Metallic Eclipse',
  'SW900-255 Gloss Sand Sparkle',
  'SW900-261 Satin Energetic Yellow',
  'SW900-316 Satin Stunning Orange',
  'SW900-419 Gloss Metallic Spark',
  'SW900-448 Gloss Passion Red',
  'SW900-568 Gloss Fun Purple',
  'SW900-629 Satin Wave Blue',
  'SW900-679 Gloss Metallic Magnetic Burst',
  'SW900-762 Gloss Metallic Radioactive',
  'SW900-767 Satin Hope Green (discontinued)',
  'SW900-716 Satin Cool Teal'
]

const FILM_THICKNESS_ROWS: ColorRow[] = [
  { code: 'SW900-109-O', color: 'White Pearl', extra: '4.2 mil (107 µm)' },
  { code: 'SW900-326-S', color: 'Gold Orange Pearlescent', extra: '4.2 mil (107 µm)' },
  { code: 'SW900-419-M', color: 'Spark', extra: '4.2 mil (107 µm)' },
  { code: 'SW900-433-O', color: 'Cardinal Red', extra: '4.2 mil (107 µm)' },
  { code: 'SW900-437-S', color: 'Red Rush Pearlescent', extra: '4.2 mil (107 µm)' },
  { code: 'SW900-514-O', color: 'Bubblegum Pink Satin', extra: '4.2 mil (107 µm)' },
  { code: 'SW900-758-O', color: 'Grass Green', extra: '4.2 mil (107 µm)' },
  { code: 'SW900-762-M', color: 'Radioactive', extra: '4.2 mil (107 µm)' },
  { code: 'SW 900-194-X', color: 'Black Carbon Fiber 4', extra: '4.2 mil (107 µm)' },
  { code: 'SW900 Diamond line', color: 'All colors', extra: '5.4 to 5.7 mil (137 to 140 µm)' },
  { code: 'SW 900-117-O', color: 'White Pearl Satin', extra: '4.2 mil (107 µm)' },
  { code: 'SW900-415-O', color: 'Red', extra: '4.2 mil (107 µm)' },
  { code: 'SW900-427-O', color: 'Soft Red', extra: '4.2 mil (107 µm)' },
  { code: 'SW900-436-O', color: 'Carmine Red', extra: '4.2 mil (107 µm)' },
  { code: 'SW900-438-O', color: 'Satin Carmine Red', extra: '4.2 mil (107 µm)' },
  { code: 'SW900-731-O', color: 'Lime Green', extra: '4.2 mil (107 µm)' },
  { code: 'SW900-759-O', color: 'Satin Grass Green', extra: '4.2 mil (107 µm)' },
  { code: 'SW900-777-S', color: 'Light Green Pearlescent', extra: '4.2 mil (107 µm)' },
  { code: 'SW 900-115-X', color: 'White Carbon Fiber', extra: '4.2 mil (107 µm)' },
  { code: 'SW900 Rugged line', color: 'All colors', extra: '4.5 mil (114 µm)' }
]

const OPACITY_ROWS: ColorRow[] = [
  { code: 'SW900-225-O', color: 'Yellow', extra: 'Gloss' },
  { code: 'SW900-245-O', color: 'Dark Yellow', extra: 'Gloss' },
  { code: 'SW900-321-O', color: 'Matte Orange', extra: 'Matte' },
  { code: 'SW900-372-O', color: 'Satin Orange', extra: 'Satin' },
  { code: 'SW900-370-O', color: 'Orange', extra: 'Gloss' },
  { code: 'SW900-371-M', color: 'Blaze Orange Matte Metallic', extra: 'Matte' },
  { code: 'SW900-226-O', color: 'Satin Yellow', extra: 'Satin' },
  { code: 'SW900-234-O', color: 'Ambulance yellow', extra: 'Gloss' },
  { code: 'SW900-438-O', color: 'Carmine Red Satin', extra: 'Satin' },
  { code: 'SW900-444-M', color: 'Cherry Red Matte Metallic', extra: 'Matte' },
  { code: 'SW900-472-M', color: 'Garnet Matte Metallic', extra: 'Matte' },
  { code: 'SW900-624-S', color: 'Bahama Blue Pearlescent', extra: 'Gloss' },
  { code: 'SW900-745-M', color: 'Green Apple Matte Metallic', extra: 'Matte' }
]

// ---------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------

export function AverySw900Docs() {
  const [isPosterOpen, setIsPosterOpen] = useState(false)

  return (
    <div className='space-y-8'>
      {/* Poster: visible by default, not collapsed */}
      <div>
        <h3 className='text-base font-semibold text-foreground mb-3'>Colour Swatch Poster</h3>
        <button
          type='button'
          onClick={() => setIsPosterOpen(true)}
          className='group relative block w-full max-w-sm cursor-zoom-in overflow-hidden rounded-lg border border-divider'
          aria-haspopup='dialog'
          aria-label='Expand the SW900 colour swatch poster'
        >
          <img
            src={POSTER_DISPLAY_URL}
            alt={POSTER_ALT}
            loading='lazy'
            decoding='async'
            className='w-full h-auto object-cover transition-transform group-hover:scale-[1.02]'
          />
          <span className='absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1.5 text-xs font-medium text-white'>
            <Icon icon='fullscreen' className='w-4 h-4' />
            Expand
          </span>
        </button>
      </div>

      {/* Product Data Sheet: collapsed accordion, PDF section order */}
      <div>
        <h3 className='text-base font-semibold text-foreground mb-3'>Product Data Sheet</h3>
        <p className='text-xs text-muted mb-3'>Revision 20, dated 03/10/26</p>
        <DisclosureGroup allowsMultipleExpanded className='flex flex-col gap-3'>
          <DocSection id='uses-construction' title='Uses & Construction'>
            <p>
              Avery Dennison SW900 Easy Apply Cast Vinyl Film Series is a premium quality cast film designed for
              use in vehicle and graphics markets where high quality film finish and cost-effective full color
              wrapping is required.
            </p>
            <p>
              The patented Avery Dennison Easy Apply RS feature allows for faster positioning, bubble free
              application, and long term removability.
            </p>
            <KeyValueTable
              rows={[
                { label: 'Face Film', value: '3.2 mil (80 µm) to 4.7 mil (119 µm) high gloss, satin or matte cast film' },
                { label: 'Adhesive', value: 'Long Term Removable Acrylic' },
                { label: 'Liner', value: '90# StaFlat, Poly-Coated with Easy Apply RS technology' },
                { label: 'Durability', value: 'See durability table starting on page 6' },
                { label: 'Shelf Life', value: '2 years from the date of manufacture' },
                { label: 'Application Surfaces', value: 'Flat, Flat with Rivets, Corrugations, Deep Recesses' }
              ]}
            />
            <p className='text-xs text-muted'>
              *NOTE: Films with face thickness 4 mil (102 µm) and above may have reduced conformability.
            </p>
          </DocSection>

          <DocSection id='features' title='Features'>
            <BulletList
              items={[
                'SW900 Gloss, satin or matte, super conformable film',
                'SW900X Extreme Textures includes Carbon Fiber, Brushed Metallic, and Rugged',
                'Unique patented adhesive technology',
                <>
                  Superior conformability to irregular substrates
                  <ul className='list-disc space-y-1 pl-5 mt-1.5'>
                    <li>Deeply concave shaped</li>
                    <li>Extremely convex shaped</li>
                    <li>Best conformance in channels</li>
                  </ul>
                </>,
                'Outstanding durability and outdoor performance',
                'Excellent UV, temperature, humidity, and salt-spray resistance',
                'SW900 custom colors available (contact your sales representative)',
                'Easy Apply feature helps prevent wrinkles and bubbles during application',
                'Excellent removability'
              ]}
            />
          </DocSection>

          <DocSection id='common-applications' title='Common Applications'>
            <BulletList items={['Truck', 'Cars & Vans', 'Buses', 'Trailer', 'Emergency Vehicles']} />
          </DocSection>

          <DocSection id='physical-characteristics' title='Physical Characteristics'>
            <p className='text-xs text-muted'>
              Data represents average values where applicable, and is not intended for specification purposes.
            </p>
            <KeyValueTable
              rows={[
                { label: 'Caliper, face', value: '3.2 mil (80 µm) to 4.7 mil (119 µm). Reference Color & Durability Chart in this document for caliper for specific film. *NOTE: Films with face thickness 4 mil (102 micron) and above may have reduced conformability.' },
                { label: 'Caliper, adhesive', value: '1.2 mil (30 µm)' },
                { label: 'Dimensional stability', value: '<0.010"(0.254 mm)' },
                { label: 'Tensile at Yield', value: '4.0 - 8.0 lb/in (0.7-1.5 kg/cm)' },
                { label: 'Elongation', value: '150% at break (120% for metallic)' },
                { label: 'Gloss', value: 'Hunter Gloss @ 60 degrees: Gloss - >85 GU, Satin - 20-30 GU, Matte - <15 GU' },
                { label: 'Adhesion', value: '15 min 2.1lbs/in (367N/m); 24 hr 2.5 lbs/in (437 N/m); 1 week 3.0 lbs/in (525 N/m)' },
                { label: 'Flammability', value: 'FMVSS 302 Self Extinguishing; ASTM E-84 Class 1 or A' },
                { label: 'Shelf-Life', value: '2 years from the date of manufacture (when stored at the following temperatures and humidity conditions 68°-77° F (20° - 25° C) and 50±5%R. H.)' },
                { label: 'Durability', value: 'Vertical Exposure. Reference color and durability charts in this data sheet. See page 6' },
                { label: 'Min. Application Temperature', value: '50° F (10°C) Flat & Simple Curves; 60°F (16° C) Complex curves' },
                { label: 'Service Temperature', value: '-50° to 180°F (-45° to 82° C) (Reasonable range of temperatures which would be expected under normal environmental conditions).' },
                { label: 'Chemical resistance', value: 'Resistant to most mild acids, alkalis, and salt solutions' },
                { label: 'Removable', value: 'For the length of the applicable durability period. Reference durability charts in this data sheet. See Page 6' },
                { label: 'Compliance', value: 'California Proposition 65' },
                { label: 'MSDS', value: 'MSDS Article Letter' }
              ]}
            />
          </DocSection>

          <DocSection id='related-documents' title='Related Documents'>
            <p>
              The following Avery Dennison literature will provide necessary information to the user for proper
              application, storage, and other requirements and is available upon request from your Avery
              Dennison representative or from the Avery Dennison website (www.graphics.averydennison.com).
            </p>
            <KeyValueTable
              rows={[
                { label: 'Substrate Cleaning and Preparation', value: 'Instructional Bulletin #1.10' },
                { label: 'Storage, Maintenance, and Cleaning of PVC Films', value: 'Instructional Bulletin #1.20' },
                { label: 'Cleaning and Maintenance of Vehicle Wraps', value: 'Instructional Bulletin #1.50' },
                { label: 'Application of Avery Dennison SW 900 Series Films', value: 'Instructional Bulletin #4.08' },
                { label: 'Premask Recommendations', value: 'Instructional Bulletin #5.50' },
                { label: 'Removal Instructions', value: 'Instructional Bulletin #4.10' }
              ]}
            />
          </DocSection>

          <DocSection id='application' title='Application'>
            <p>
              Avery Dennison SW900 Wrapping Film is designed for application on to solid surfaces such as
              passenger vehicles and buses.
            </p>
            <p className='font-medium text-foreground'>Important Note: Never use a wet application method to apply this film.</p>
            <p>
              Some slight color shift may occur between rolls or lots, therefore, minimizing the mixing of
              rolls/lots on a specific vehicle is encouraged. If multiple rolls are needed to be sure to use only
              rolls from the same production lot.
            </p>
            <p>
              All vehicle substrates must be cleaned according to Avery Dennison recommended cleaning practices
              as outlined in Instructional Bulletin #1.10 &ldquo;Substrate Cleaning and Preparation&rdquo;.
            </p>
            <p>
              Due to the unique finish and manufacturing process of metallic and pearlescent films, variances in
              color can be visible when the material is oriented in different directions. When utilizing metallic,
              pearlescent and Color Flow films, always keep the application direction of the film consistent with
              the orientation of the film when cut from the roll and applied to the vehicle. One roll should be
              used per vehicle, wherever possible to minimize any possible color shifts between rolls or lots.
            </p>
            <p>
              See Instructional Bulletin #4.08 &ldquo;Application Avery Dennison SW 900 (Supreme Wrap) Film&rdquo;
              for complete application recommendations. For cleaning and care recommendations see Instructional
              Bulletin #1.50.
            </p>
            <p>
              NOTE: SW900 Series is designed as a wrap film. Cutting for the purpose of preparing sizes or shapes
              has proven to be feasible. However, sign cutting of letters and graphics is not recommended.
            </p>
            <p>
              Important Note: Only use Avery Dennison recommended cleaning products and solutions (noted in
              Instructional Bulletin #1.50). Cleaning and maintenance products should conform to the following:
            </p>
            <BulletList
              items={[
                'Free from abrasive components',
                'Ideally pH balanced, thus not highly acidic or alkaline (pH level between 5 and 9)',
                'Free from alcohol (non isopropyl), acids, ammonia, chlorine, glycol ethers, harmful detergents, petroleum distillates and phosphates'
              ]}
            />
          </DocSection>

          <DocSection id='unsuitable-uses' title='Unsuitable Uses'>
            <p>
              This Avery Dennison product is not designed or recommended for the following uses. Unsuitable
              applications or exposure conditions include, but is not limited to:
            </p>
            <BulletList
              items={[
                'Paint that is not thoroughly cured or dried',
                'Low surface energy substrates (i.e. Tedlar® coatings, rubber, unpainted plastic etc)',
                'Substrate surfaces that are not clean and smooth (little or no variation in texture)',
                'Painted substrates with poor paint-to-substrate, or paint-to-paint bond',
                'Stainless steel',
                'Film applied to non-Avery Dennison Films',
                'Film applied to pre-existing graphics (pre-approval required)',
                'Watercraft below the static water line',
                'Watercraft which are not edge sealed',
                'Non-OEM painted vehicles',
                'Graphic removal from paint with poor adhesion or existing graphics',
                'Graphics exposed to oil, harsh chemicals, or gasoline vapors or spills',
                'Graphics where application tape must adhere to liner',
                'Sign cutting of letters and graphics'
              ]}
            />
          </DocSection>

          <DocSection id='removability' title='Removability'>
            <p>
              The removability listed in the physical characteristics section &ldquo;Clean removability&rdquo;
              means removable with less than 20% adhesive residue when using appropriate heat and chemical
              removal methods. See Instructional Bulletin #4.10 for removal instructions.
            </p>
            <p>Avery Dennison does not warrant removability from the following substrates:</p>
            <BulletList
              items={[
                'Surfaces with poor paint-to-substrate adhesion',
                'Wallboard (painted or unpainted)',
                'Pre existing graphics that must remain intact; damage to existing graphic when film is removed',
                'Improperly cured paint',
                'Oxidized or chalked substrates',
                'Stainless Steel'
              ]}
            />
            <p>Avery Dennison makes no warranty for:</p>
            <BulletList
              items={[
                'Paint/Clear Coat Staining: Avery Dennison does not warrant vehicle paint staining that may be visible after removing material which has cracked or discolored. At the first sign of a change in the surface of the material contact your Avery Dennison Sales or Technical Representative.',
                'Ease or speed of removal of any graphic',
                'Removal from automotive paint that is greater than 5 years old',
                'Removal from paint that is improperly cured',
                'Removal from aged paint or metals, surface oxidation or chalking; user must test, approve, and accept liability for such applications',
                'Saltwater marine applications',
                'Marine applications below the water line'
              ]}
            />
          </DocSection>

          <DocSection id='application-to-glass' title='Application to Glass'>
            <BulletList
              items={[
                'Because of the unique properties and varieties of glass, special considerations must be considered for windows and graphic applications. Avery Dennison accepts no liability for glass breakage.',
                'Glass absorbs heat when exposed to sunlight. The degree of absorption across windows can vary because of shading, heating, and cooling ventilation, and insulation from window frames. These temperature differences across the window produce stress, which can cause glass breakage. The ability of glass to resist breakage because of temperature stress is affected by window size, glass thickness, glass treatments, quality, and design.',
                'Window cleaning methods also vary, from the type of cleaners used, to the washing methods employed. Specific chemicals used in window cleaners may affect the adhesion of pressure-sensitive graphics. Power washing methods may impact the adhesion of graphics, if the water pressure or temperature exceeds the graphic’s adhesion capability. Power washing window graphics is not recommended.'
              ]}
            />
            <p>
              NOTE: Avery Dennison does not recommend the use of SW900 Wrapping Film to be used as block out
              film for window applications.
            </p>
          </DocSection>

          <DocSection id='durability' title='Durability'>
            <p>
              Durability is the period of time the product is expected to perform satisfactorily when applied in
              vertical exposure conditions by zone as defined in Instructional Bulletin 1.30, provided that the
              film is properly stored, converted and installed in accordance with Avery Dennison guidelines. The
              term of Avery Dennison&rsquo;s warranty is equal to the product&rsquo;s durability.
            </p>
            <p>
              Abrasion and Loss of Gloss are not covered by any Avery Dennison Warranty. These changes are
              considered normal wear and tear.
            </p>
            <p>
              Other factors which may reduce the durability of this product series include: surface texture,
              surface preparation, application method, angle and direction of the sun, environmental conditions,
              cleaning &amp; maintenance.
            </p>
            <p>
              Products containing petroleum distillates or solvents such as soaps, waxes, or protective coatings,
              void any warranty, and may cause premature failure of the material.
            </p>
            <p>
              Matte finished materials are subject to extreme color and gloss shift when exposed to cleaners,
              wax, and polish. Test an inconspicuous area prior to using any cleaners, wax, and polish. The end
              user assumes complete responsibility to ensure that matte finishes are not affected during cleaning
              or protecting.
            </p>
          </DocSection>

          <DocSection id='sw900-durability-table' title='SW900 & SW900X Durability'>
            <div className='overflow-x-auto rounded-lg border border-divider'>
              <table className='w-full min-w-[640px] border-collapse text-sm'>
                <thead>
                  <tr className='border-b border-divider text-left'>
                    <th rowSpan={2} className='px-3 py-2 align-bottom font-medium text-muted'></th>
                    <th colSpan={2} className='px-3 py-2 text-center font-medium text-muted'>Zone 1</th>
                    <th colSpan={2} className='px-3 py-2 text-center font-medium text-muted'>Zone 2</th>
                    <th colSpan={2} className='px-3 py-2 text-center font-medium text-muted'>Zone 3</th>
                  </tr>
                  <tr className='border-b border-divider text-left'>
                    <th className='px-3 py-2 font-medium text-muted'>Vertical</th>
                    <th className='px-3 py-2 font-medium text-muted'>Horizontal Non-Vertical</th>
                    <th className='px-3 py-2 font-medium text-muted'>Vertical</th>
                    <th className='px-3 py-2 font-medium text-muted'>Horizontal Non-Vertical</th>
                    <th className='px-3 py-2 font-medium text-muted'>Vertical</th>
                    <th className='px-3 py-2 font-medium text-muted'>Horizontal Non-Vertical</th>
                  </tr>
                </thead>
                <tbody>
                  {DURABILITY_ROWS.map((row, i) => (
                    <tr key={row.category} className={i % 2 === 0 ? 'bg-overlay' : ''}>
                      <td className='px-3 py-2 text-foreground'>{row.category}</td>
                      <td className='px-3 py-2 whitespace-nowrap text-foreground'>{row.zone1Vertical}</td>
                      <td className='px-3 py-2 whitespace-nowrap text-foreground'>{row.zone1Horizontal}</td>
                      <td className='px-3 py-2 whitespace-nowrap text-foreground'>{row.zone2Vertical}</td>
                      <td className='px-3 py-2 whitespace-nowrap text-foreground'>{row.zone2Horizontal}</td>
                      <td className='px-3 py-2 whitespace-nowrap text-foreground'>{row.zone3Vertical}</td>
                      <td className='px-3 py-2 whitespace-nowrap text-foreground'>{row.zone3Horizontal}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className='text-xs text-muted'>Reference IB 1.30 for zones by geographic location.</p>
            <BulletList
              items={[
                <>Non-vertical: The face of the graphic is between 10° to 45º from vertical. Specifically for Supreme Wrapping Film (SW900) Non-vertical exposure performance will be calculated using the Horizontal exposure value.</>,
                <>Horizontal: The face of the graphic is between 45° and 90º from vertical, for Supreme Wrapping Film (SW900) Horizontal applications will be warranted as stated in this ICS Performance Guarantee Bulletin.</>,
                <>Vertical: The face of the graphic is between 0 and 10 degrees from vertical.</>
              ]}
            />
            <p className='pt-2 text-xs font-medium text-foreground'>*Colors with gold flake include:</p>
            <div className='grid grid-cols-1 gap-x-6 gap-y-1 text-xs text-foreground/80 sm:grid-cols-3'>
              {GOLD_FLAKE_COLORS.map(name => (
                <span key={name}>{name}</span>
              ))}
            </div>
          </DocSection>

          <DocSection id='film-thickness-conformability' title='Film Thickness / Conformability'>
            <p>
              Films with face thickness 4 mil (102 micron) and above may have reduced conformability. These
              films include:
            </p>
            <ColorTable headers={['Name', 'Color', 'Face Caliper']} rows={FILM_THICKNESS_ROWS} />
            <p>
              Contact your Avery Dennison approved distributor, or samples.graphics@averydennison.com, your
              sales or customer service contact at Avery Dennison for 5&rdquo; x 8&rdquo; color swatches for
              review prior to use.
            </p>
            <p className='text-xs text-muted'>
              Avery Dennison is a registered trademark of Avery Dennison Corporation Patent registration:
              US6,630,049, US7,060,351, US7,344,618, US7,332,205, EP1276605, EP1282472
            </p>
            <p className='text-xs text-muted'>Revisions are italicized</p>
            <p className='font-semibold text-foreground'>WARNING</p>
            <p>
              Failure to install the film in full compliance with Avery Dennison&rsquo;s installation
              instructions may result in personal injury or property damage. Read and follow all instructions
              when installing the film.
            </p>
          </DocSection>

          <DocSection id='opacity' title='Opacity'>
            <p>
              Most colors are considered opaque; however it is up to the end user to determine if the level of
              opacity meets their needs. The following colors may be considered less than fully opaque, and may
              demonstrate color shift when applied, or allow show through of paint lines of dissimilar colors,
              or overlap of material:
            </p>
            <ColorTable headers={['Name', 'Color', 'Finish']} rows={OPACITY_ROWS} />
          </DocSection>

          <DocSection id='warranty' title='Warranty'>
            <p>
              This Warranty applies to the Product listed in this Data Sheet. All statements, technical
              information (including physical and chemical characteristics) and recommendations about Avery
              Dennison products are based upon tests believed to be reliable but do not constitute a guarantee or
              warranty. All Avery Dennison products are sold subject to the Purchaser&rsquo;s assent and
              agreement that Purchaser is responsible for, and has independently determined, the suitability of
              such products for its purposes or its customer&rsquo;s purposes. Avery Dennison products are
              warranted to be free from defects in material and workmanship (i) for two years from the date of
              manufacture, or (ii) from the date of manufacture until the expiration of the period stated on the
              specific Product Data Sheet in effect at the time of delivery.. Such time periods are subject in
              either case to the proper storing and application of said product, and the failure to properly
              store or apply the product, including without limitation the failure to follow any applicable
              Instructional Bulletin, negates any warranty. It is expressly agreed and understood that Avery
              Dennison&rsquo;s sole obligation and Purchaser&rsquo;s exclusive remedy under this warranty, under
              any other warranty, express or implied, or otherwise, shall be limited exclusively to: (a) repair
              or replacement of defective product without charge at Avery Dennison&rsquo;s plant or at the
              location of the product (at Avery Dennison&rsquo;s election), or (b) in the event repair or
              replacement are not commercially practical, a credit amount up to the price of the product taking
              into account the defect in the product in Avery Dennison&rsquo;s sole discretion.
            </p>
            <p className='text-xs uppercase text-muted'>
              The warranty expressly set forth above is in lieu of any other warranties, express or implied,
              including, but not limited to, any implied warranties of merchantability, fitness for any
              particular use and/or non-infringement. Seller specifically disclaims and excludes all other such
              warranties. No waiver, alteration, addition or modification of the foregoing conditions shall be
              valid unless made in writing and manually signed by an officer of Avery Dennison.
            </p>
            <p className='text-xs uppercase text-muted'>
              Avery Dennison&rsquo;s liability for defective products shall not exceed the purchase price paid
              therefore by purchaser and in no event shall Avery Dennison be responsible for any incidental or
              consequential damages whether foreseeable or not, caused by defects in such product, whether such
              damage occurs or is discovered before or after replacement or credit, and whether or not such
              damage is caused by Avery Dennison&rsquo;s negligence.
            </p>
            <p>
              All sales and contracts for sale of Products are subject to Purchaser&rsquo;s assent and agreement
              to this Warranty. Avery Dennison hereby objects to any terms or conditions contained in any
              purchase order or other communication of any kind from the Purchaser that is conflicting,
              inconsistent or additional to this Agreement.
            </p>
          </DocSection>
        </DisclosureGroup>
      </div>

      <Modal
        isOpen={isPosterOpen}
        onClose={() => setIsPosterOpen(false)}
        title='Colour Swatch Poster'
        size='full'
        backdrop='blur'
        scrollBehavior='inside'
        closeButton
      >
        <img
          src={POSTER_FULL_URL}
          alt={POSTER_ALT}
          loading='lazy'
          decoding='async'
          className='mx-auto max-h-[85vh] w-auto max-w-full object-contain'
        />
      </Modal>
    </div>
  )
}
